import React, { useContext, useEffect, useState } from 'react';
import { AptunoButton, HeaderSimple, Icon, SIZES, Stepper, VARIANTS } from '@aptuno/aptuno-ui';
import { useTrackingSource } from '@contexts/Shared/utils';
import {
  ApplicationStateSteps,
  DocumentUploaded,
  estateApplicationStageContext,
  EstateApplicationSteps,
  RiskStudyDocumentType,
  useCreditStudyActions,
  useDocumentationActions,
} from '@contexts/Estate';
import { ActionsBarRoot, ContentBox, FormWrapper, IconClose, ProcessContainer } from '../EstateApplication.styled';
import { TitleText } from './ApplicantDocuments.styled';
import { DocumentsUploadForm } from './DocumentsUploadForm/DocumentsUploadForm';
import { DeleteDocumentCommand, DocumentationType, DocumentId } from '@contexts/Estate/EstateApplication/domain';
import { Response } from '@shared/models';
import { useAnalyticsTracker } from '@services/analytics';
import { pipe } from 'fp-ts/lib/function';
import { ApplicationCompleted } from '@contexts/Estate/EstateApplication/domain/events/application-completed.event ';

export const ApplicantDocumentsUpload = ({ isIsolatedPage = false, isIndependentRiskStudy = false }) => {
  const currentStep = 2;
  const estateApplication = useContext(estateApplicationStageContext);
  const {
    result: providedUserInformation,
    loading: loadingProvidedUserInfo,
    recall: reloadUserInformation,
  } = useCreditStudyActions().creditStudyQueryActions.useGetProvidedUserInformation();

  const {
    result: exposedDocumentation,
    recall: recallExposedDocumentation,
    loading: isLoadingExposedDocuments,
    get: getExposedDocumentation,
  } = useDocumentationActions().documentQueryActions.useGetExposedDocumentation();
  const { documentAdded, addDocument } = useDocumentationActions().documentsCommandActions.useAddDocument();
  const { documentDeleted, deleteDocument } = useDocumentationActions().documentsCommandActions.useDeleteDocument();
  const { documentReplaced, replaceDocument } = useDocumentationActions().documentsCommandActions.useReplaceDocument();
  const [loadingDocumentTypes, setLoadingDocumentTypes] = useState<RiskStudyDocumentType[]>([]);
  const eventTracker = useAnalyticsTracker();
  const originSource = useTrackingSource();

  useEffect(() => {
    if (!loadingProvidedUserInfo && Response.isNotPerformed(providedUserInformation)) {
      reloadUserInformation();
    } else if (Response.isSucceed(providedUserInformation)) {
      getExposedDocumentation({
        creditStudyId: providedUserInformation.result.creditStudyId,
      });
    }
  }, [loadingProvidedUserInfo, providedUserInformation.type]);

  useEffect(() => {
    if (Response.isSucceed(exposedDocumentation) && exposedDocumentation.result.type === DocumentationType.UNKNOWN) {
      recallExposedDocumentation();
    }
  }, [exposedDocumentation.type]);

  useEffect(() => {
    if (!!documentAdded || !!documentDeleted || !!documentReplaced) recallExposedDocumentation();
  }, [documentAdded, documentDeleted, documentReplaced]);

  useEffect(() => {
    if (loadingDocumentTypes.length > 0 && Response.isSucceed(exposedDocumentation)) {
      setLoadingDocumentTypes((loadingDocuments) => {
        const alreadyAddedDocuments = exposedDocumentation.result.documents;
        const newLoadingDocumentTypes = loadingDocuments.filter(
          (type) => !alreadyAddedDocuments.some((doc) => doc.type === type)
        );

        return newLoadingDocumentTypes;
      });
    }
  }, [exposedDocumentation['result']?.documents, loadingDocumentTypes.length]);

  const onAddDocument = (newFiles: File[], type: RiskStudyDocumentType) => {
    if (Response.isSucceed(providedUserInformation)) {
      const documentToUpload = {
        file: newFiles[0],
        creditStudyId: providedUserInformation.result.creditStudyId,
        documentType: type,
      };

      setLoadingDocumentTypes((prevState) => [...prevState, type]);
      addDocument(documentToUpload);
    }
  };

  const onDeleteDocument = (documentId: DocumentId) => {
    if (Response.isSucceed(providedUserInformation)) {
      const command: DeleteDocumentCommand = {
        creditStudyId: providedUserInformation.result.creditStudyId,
        documentId,
      };

      deleteDocument(command);
    }
  };

  const onReplaceDocument = (newFiles: File[], type: RiskStudyDocumentType) => {
    const previouslyUploadedDocument = getDocumentUploaded(type);

    if (Response.isSucceed(providedUserInformation) && previouslyUploadedDocument) {
      const documentToReplace = {
        file: newFiles[0],
        creditStudyId: providedUserInformation.result.creditStudyId,
        documentId: previouslyUploadedDocument.id,
      };

      replaceDocument(documentToReplace);
      setLoadingDocumentTypes(() => [type]);
    }
  };

  const getDocumentUploaded = (documentType: RiskStudyDocumentType): DocumentUploaded | undefined => {
    return Response.isSucceed(exposedDocumentation)
      ? exposedDocumentation.result.documents.find((doc) => doc.type === documentType)
      : undefined;
  };

  const handleNextStepClick = () => {
    setIsFormTouched(true);

    if (isCompletedApplication) {
      segmentTrack();
      estateApplication?.setCurrentState(ApplicationStateSteps.successfulApplication);
    }
  };

  const [isFormTouched, setIsFormTouched] = useState(false);

  const isCompletedApplication =
    Response.isSucceed(exposedDocumentation) && exposedDocumentation.result.isComplete.value;

  const handleOnLeave = () => {
    estateApplication?.setCurrentState(ApplicationStateSteps.leaveApplication);
  };

  const segmentTrack = () =>
    pipe(
      providedUserInformation,
      Response.asOption((user) => eventTracker.trackEvent(new ApplicationCompleted(originSource, user.userId.value)))
    );

  const uploadedFiles = Response.isSucceed(exposedDocumentation) ? exposedDocumentation.result.documents : [];

  return (
    <div>
      <HeaderSimple withBorderRadius={false} maxWidth="100%" isFixed={false}>
        <TitleText>Estudio de Riesgo</TitleText>
        <IconClose icon={'closeAlt'} color={'#9B9B9B'} marginRight={'16px !important'} onClick={handleOnLeave} />
      </HeaderSimple>
      <ProcessContainer>
        <Stepper steps={EstateApplicationSteps} activeStep={currentStep} />
      </ProcessContainer>
      <ContentBox>
        {Response.isSucceed(exposedDocumentation) && (
          <FormWrapper name="documentsForm">
            <DocumentsUploadForm
              requiredDocuments={
                Response.isSucceed(exposedDocumentation) ? exposedDocumentation.result.requiredDocuments : []
              }
              uploadedFiles={uploadedFiles}
              loadingDocumentTypes={loadingDocumentTypes}
              onAddFile={onAddDocument}
              onReplaceFile={onReplaceDocument}
              onDelete={onDeleteDocument}
              isFormTouched={isFormTouched}
            />
          </FormWrapper>
        )}
        {(loadingProvidedUserInfo || isLoadingExposedDocuments) && <div>Cargando...</div>}
        <ActionsBarRoot>
          <AptunoButton appearance={VARIANTS.PRIMARY} size={SIZES.BIGGEST} onClick={handleNextStepClick}>
            Finalizar
            <Icon width={'12px'} height={'12px'} icon="Arrow_Right" />
          </AptunoButton>
        </ActionsBarRoot>
      </ContentBox>
    </div>
  );
};
